export const mockData = {
  hero: {
    title: "Enterprise Mobile & Residential Proxies",
    subtitle: "Access the world's largest proxy network with 300M+ IPs. Unlimited mobile proxies and residential IPs starting from $1.5/GB.",
    cta: "Start Now",
    secondaryCta: "View Pricing"
  },
  
  stats: [
    { number: "300M+", label: "IP Addresses" },
    { number: "200+", label: "Countries" },
    { number: "99.9%", label: "Uptime" },
    { number: "24/7", label: "Support" }
  ],

  features: [
    {
      icon: "Shield",
      title: "Premium Residential IPs",
      description: "Access 300M+ residential IPs with country, city, and ISP targeting. Starting from $1.5/GB with volume discounts up to $0.6/GB."
    },
    {
      icon: "Smartphone",
      title: "Unlimited Mobile Proxies",
      description: "True mobile IPs with carrier rotation. 24-hour trial available for $10/proxy. Perfect for mobile app testing and social media."
    },
    {
      icon: "Globe",
      title: "Global Coverage",
      description: "200+ countries and 1000+ cities worldwide. Advanced targeting by country, region, city, and ISP for precise geo-location needs."
    },
    {
      icon: "Zap",
      title: "High Performance",
      description: "99.9% uptime with <500ms response time. Dedicated pools ensure consistent performance for enterprise applications."
    },
    {
      icon: "Lock",
      title: "Enterprise Security",
      description: "HTTPS/SOCKS5 protocols, IP whitelisting, and session management. SOC2 compliant with 24/7 monitoring."
    },
    {
      icon: "Headphones",
      title: "Expert Support",
      description: "24/7 technical support with dedicated account managers for enterprise clients. Comprehensive API documentation and SDKs."
    }
  ],

  useCases: [
    {
      slug: "proxies-for-ai",
      title: "Proxies for AI",
      icon: "Brain",
      description: "Scale AI training and data collection with reliable proxy infrastructure",
      longDescription: "Power your AI and machine learning projects with our enterprise-grade proxy infrastructure. Access diverse data sources without geographical restrictions or rate limiting.",
      benefits: ["Unlimited data collection", "Bypass AI detection", "Scale ML training", "Global data diversity"]
    },
    {
      slug: "web-scraping",
      title: "Web Scraping",
      icon: "Globe",
      description: "Extract data at scale with rotating residential and mobile IPs",
      longDescription: "Extract data from any website with our advanced proxy rotation. Avoid blocks and CAPTCHAs with residential and mobile IP pools.",
      benefits: ["Avoid blocks & bans", "High success rates", "Rotate IPs automatically", "Parse any website"]
    },
    {
      slug: "website-testing",
      title: "Website Testing",
      icon: "TestTube",
      description: "Test websites and applications from different geographic locations",
      longDescription: "Ensure your websites and applications work perfectly from any location. Test geo-restricted content and localized experiences.",
      benefits: ["Multi-location testing", "Performance monitoring", "Geo-blocking detection", "User experience validation"]
    },
    {
      slug: "travel-fare-aggregation",
      title: "Travel Fare Aggregation",
      icon: "Plane",
      description: "Compare prices and availability across travel booking platforms",
      longDescription: "Access real-time travel pricing data from airlines, hotels, and booking platforms. Provide accurate price comparisons to your customers.",
      benefits: ["Real-time pricing", "Avoid price discrimination", "Multiple platforms", "Competitive analysis"]
    },
    {
      slug: "market-research",
      title: "Market Research",
      icon: "TrendingUp",
      description: "Gather market intelligence and competitor data at scale",
      longDescription: "Collect comprehensive market data and competitor intelligence. Monitor pricing, inventory, and market trends across global markets.",
      benefits: ["Competitor monitoring", "Price tracking", "Inventory checking", "Market analysis"]
    },
    {
      slug: "google-proxies",
      title: "Google Proxies",
      description: "Specialized proxies optimized for Google services and SERP data",
      longDescription: "Access Google Search, Ads, Shopping, and other Google services without restrictions. Optimized for SERP scraping and SEO monitoring.",
      benefits: ["Google-optimized", "SERP data collection", "Ad verification", "SEO monitoring"]
    },
    {
      slug: "access-restricted-content",
      title: "Access Restricted Content",
      icon: "Unlock",
      description: "Bypass geo-restrictions and access content from anywhere",
      longDescription: "Access geo-restricted content, streaming platforms, and region-locked websites. Perfect for content verification and global market research.",
      benefits: ["Bypass geo-blocks", "Content verification", "Global access", "Streaming support"]
    },
    {
      slug: "privacy-advantages",
      title: "Privacy Advantages",
      icon: "Eye",
      description: "Protect your identity and maintain anonymity online",
      longDescription: "Maintain complete privacy and anonymity while browsing. Protect sensitive research and business intelligence gathering operations.",
      benefits: ["Complete anonymity", "Identity protection", "Secure browsing", "Business intelligence"]
    }
  ],

  pricing: {
    residential: {
      title: "Residential Proxies",
      subtitle: "Premium residential IPs with global coverage",
      plans: [
        {
          name: "Starter",
          price: "$1.30",
          unit: "per GB",
          volume: "1GB - 30GB",
          monthlyFrom: "$1.30",
          features: ["300M+ residential IPs", "Country targeting", "City targeting", "ISP targeting", "HTTP/HTTPS support", "24/7 support", "99.9% uptime"],
          popular: false,
          savings: null
        },
        {
          name: "Professional", 
          price: "$1.10",
          unit: "per GB",
          volume: "30GB - 100GB",
          monthlyFrom: "$33",
          features: ["All Starter features", "City targeting", "ISP targeting", "Priority support", "Dedicated pools", "API access"],
          popular: true,
          savings: "15%"
        },
        {
          name: "Business",
          price: "$1.00",
          unit: "per GB", 
          volume: "100GB - 1TB",
          monthlyFrom: "$100",
          features: ["All Professional features", "Custom integrations", "Account manager", "SLA guarantee", "Private pools", "Advanced analytics"],
          popular: false,
          savings: "23%"
        },
        {
          name: "Scale",
          price: "$0.90",
          unit: "per GB",
          volume: "1TB - 5TB", 
          monthlyFrom: "$900",
          features: ["All Business features", "Priority routing", "Custom endpoints", "White-label options", "Dedicated infrastructure", "24/7 phone support"],
          popular: false,
          savings: "31%"
        },
        {
          name: "Enterprise",
          price: "Custom",
          unit: "contact sales",
          volume: "5TB+",
          monthlyFrom: "Custom",
          features: ["All Scale features", "Volume discounts", "Custom contracts", "On-premise options", "Compliance support", "Training & onboarding"],
          popular: false,
          savings: "Up to 50%"
        }
      ]
    },
    mobile: {
      title: "Unlimited Mobile Proxies",
      subtitle: "True mobile IPs with carrier rotation",
      plans: [
        {
          name: "Trial",
          price: "$10",
          unit: "per proxy/24h",
          volume: "24 hours",
          monthlyFrom: "$10",
          features: ["Real mobile IPs", "Carrier rotation", "4G/5G networks", "Basic support", "52+ countries available"],
          popular: true,
          savings: null
        },
        {
          name: "Monthly",
          price: "$50",
          unit: "per proxy/month",
          volume: "30 days",
          monthlyFrom: "$50",
          features: ["All Trial features", "Extended usage", "Priority support", "Custom rotation", "52+ global locations", "Sticky sessions"],
          popular: false,
          savings: null
        },
        {
          name: "Enterprise",
          price: "Custom",
          unit: "contact sales",
          volume: "Volume pricing",
          monthlyFrom: "Custom",
          features: ["Dedicated mobile pools", "SLA guarantee", "24/7 support", "Custom solutions", "Private APN", "Bulk discounts"],
          popular: false,
          savings: "Up to 40%"
        }
      ]
    }
  },

  locations: {
    countries: [
      "United States", "United Kingdom", "Germany", "France", "Canada", "Australia", 
      "Japan", "South Korea", "Brazil", "Mexico", "Spain", "Italy", "Netherlands", 
      "Sweden", "Norway", "Denmark", "Finland", "Poland", "Czech Republic", "Austria",
      "Switzerland", "Belgium", "Ireland", "Portugal", "India", "Singapore", "Hong Kong",
      "Taiwan", "Thailand", "Malaysia", "Philippines", "Indonesia", "Vietnam", "Turkey",
      "Israel", "South Africa", "Argentina", "Chile", "Colombia", "Peru", "Russia",
      "Ukraine", "Romania", "Bulgaria", "Hungary", "Croatia", "Serbia", "Slovenia",
      "Slovakia", "Lithuania", "Latvia", "Estonia", "Greece", "Cyprus"
    ],
    totalCountries: 52,
    totalCities: 1000,
    mobileCarriers: ["Verizon", "AT&T", "T-Mobile", "Sprint", "Vodafone", "O2", "EE", "Three", "Orange", "Deutsche Telekom", "TIM", "Movistar", "Bell", "Rogers", "Telstra", "Optus", "SoftBank", "KDDI", "SK Telecom", "KT Corporation"]
  },

  testimonials: [
    {
      name: "Sarah Chen",
      role: "CTO at DataFlow",
      company: "DataFlow Analytics",
      content: "IP9's residential proxy network has been instrumental in scaling our data collection operations. The reliability and coverage are unmatched.",
      rating: 5
    },
    {
      name: "Marcus Rodriguez",
      role: "Lead Engineer",
      company: "TravelBot Inc",
      content: "The travel fare aggregation accuracy improved 40% after switching to IP9. Their geo-targeting is precise and performance is consistent.",
      rating: 5
    },
    {
      name: "Emily Watson",
      role: "Product Manager",
      company: "AI Research Lab",
      content: "IP9's mobile proxies are perfect for our AI training data collection. The carrier rotation and global coverage give us the diversity we need.",
      rating: 5
    }
  ],

  faq: [
    {
      question: "What makes IP9 different from other proxy providers?",
      answer: "IP9 offers the world's largest residential proxy network with 300M+ IPs, competitive pricing starting at $1.5/GB, and specialized mobile proxy solutions. Our infrastructure is designed for enterprise-scale operations with 99.9% uptime."
    },
    {
      question: "Do you offer a free trial?",
      answer: "Yes! We offer a 24-hour mobile proxy trial for $10/proxy and provide detailed testing for residential proxies. Contact our sales team for custom trial arrangements."
    },
    {
      question: "What targeting options are available?",
      answer: "We support comprehensive targeting including country, region, city, ISP, and carrier-level targeting. Our network covers 200+ countries with granular location control."
    },
    {
      question: "How does volume pricing work?",
      answer: "Residential proxy pricing decreases with volume: $1.5/GB for 1-99GB, down to $0.6/GB for 5000GB+. Enterprise clients can negotiate custom rates for larger volumes."
    },
    {
      question: "What protocols do you support?",
      answer: "We support HTTP, HTTPS, and SOCKS5 protocols. All connections are encrypted and secure with optional IP whitelisting and authentication methods."
    }
  ]
};